/**
 * Airport Ride Booking Page
 * Features:
 * - Config-driven curated destinations with flat-rate USD pricing
 * - Quick pick buttons for popular destinations
 * - Custom address input with GPS support
 * - USD flat-rate pricing
 * - Isolated from package system
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Plane, Building2, MapPin, Calendar, Clock, 
  DollarSign, AlertCircle, CheckCircle, Users, Briefcase,
  Navigation, Search, X, Loader2, PenLine, Star, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AddressInput from '../../components/shared/AddressInput';
import { toast } from 'sonner';
import RideMapPreview from './components/RideMapPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AirportRide = () => {
  const navigate = useNavigate();
  const { token, user, isAuthenticated } = useAuth();
  
  // Data from API
  const [airports, setAirports] = useState({});
  const [kinDestinations, setKinDestinations] = useState([]);
  const [quickPicks, setQuickPicks] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [attractions, setAttractions] = useState([]);
  
  // Form state
  const [selectedAirport, setSelectedAirport] = useState('KIN');
  const [destinationType, setDestinationType] = useState('all'); // 'all', 'hotel', 'attraction', 'area', 'custom'
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [customDestination, setCustomDestination] = useState(null); // For custom address
  const [flightNumber, setFlightNumber] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pricing state
  const [rate, setRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);
  
  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchAirports();
    fetchKinDestinations();
  }, []);

  // Fetch KIN config-driven destinations
  const fetchKinDestinations = async () => {
    try {
      const [destRes, quickRes] = await Promise.all([
        fetch(`${API_URL}/api/tourist/kin/destinations`),
        fetch(`${API_URL}/api/tourist/kin/quick-picks`)
      ]);
      
      const destData = await destRes.json();
      const quickData = await quickRes.json();
      
      if (destData.success) {
        setKinDestinations(destData.destinations || []);
      }
      if (quickData.success) {
        setQuickPicks(quickData.quick_picks || []);
      }
    } catch (err) {
      console.error('Failed to load KIN destinations:', err);
      // Fallback to legacy endpoints
      fetchLegacyData();
    }
  };

  // Legacy fetch for backward compatibility
  const fetchLegacyData = async () => {
    try {
      const [hotelsRes, attractionsRes] = await Promise.all([
        fetch(`${API_URL}/api/tourist/hotels?airport_code=KIN`),
        fetch(`${API_URL}/api/tourist/attractions?region=kingston`)
      ]);

      const hotelsData = await hotelsRes.json();
      const attractionsData = await attractionsRes.json();

      if (hotelsData.success) setHotels(hotelsData.hotels);
      if (attractionsData.success) setAttractions(attractionsData.attractions);
    } catch (err) {
      console.error('Failed to load legacy data:', err);
    }
  };

  const fetchAirports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tourist/airports`);
      const data = await response.json();
      if (data.success) setAirports(data.airports);
    } catch (err) {
      console.error('Failed to load airports:', err);
    }
  };

  // Fetch rate when destination changes (for KIN config-driven destinations)
  useEffect(() => {
    if (selectedDestination) {
      // Use the pre-configured price from the destination object
      const dest = kinDestinations.find(d => d.id === selectedDestination.id);
      if (dest && dest.price_usd) {
        setRate({
          customer_price_usd: dest.price_usd,
          internal_price_jmd: dest.price_jmd,
          parish: dest.parish,
          destination: dest.name,
          flat_rate_available: true
        });
        setRateLoading(false);
        setRateError(null);
      } else {
        fetchRate();
      }
    } else if (customDestination) {
      fetchRate();
    } else {
      setRate(null);
    }
  }, [selectedDestination, customDestination, selectedAirport]);

  const fetchRate = async () => {
    setRateLoading(true);
    setRateError(null);

    try {
      let destinationParish = 'Kingston';
      let destinationName = '';
      
      if (customDestination) {
        destinationParish = customDestination.parish || 'Kingston';
        destinationName = customDestination.address;
      } else if (selectedDestination) {
        destinationParish = selectedDestination.parish || 'Kingston';
        destinationName = selectedDestination.name;
      }

      // Try KIN-specific rate first
      if (selectedAirport === 'KIN' && destinationName) {
        try {
          const kinRateRes = await fetch(
            `${API_URL}/api/tourist/kin/rate?destination=${encodeURIComponent(destinationName)}`
          );
          const kinRateData = await kinRateRes.json();
          
          if (kinRateData.success && kinRateData.rate) {
            setRate({
              customer_price_usd: kinRateData.rate.price_usd,
              internal_price_jmd: kinRateData.rate.price_jmd,
              parish: kinRateData.rate.parish,
              destination: kinRateData.rate.destination,
              flat_rate_available: true
            });
            setRateLoading(false);
            return;
          }
        } catch (e) {
          // Fall through to generic rate
        }
      }

      // Fall back to generic parish-based rate
      const response = await fetch(
        `${API_URL}/api/tourist/airport-rate?airport_code=${selectedAirport}&destination_parish=${encodeURIComponent(destinationParish)}`
      );

      const data = await response.json();
      
      if (data.success) {
        setRate(data.rate);
      } else {
        setRateError(data.error?.message || 'Failed to get rate');
      }
    } catch (err) {
      setRateError('Network error. Please try again.');
    } finally {
      setRateLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    const bookingState = {
      selectedAirport,
      destinationType,
      selectedDestination,
      customDestination,
      flightNumber,
      arrivalDate,
      arrivalTime,
      passengers,
      luggage,
      returnTo: '/ride/airport'
    };
    localStorage.setItem('pendingAirportBooking', JSON.stringify(bookingState));
    navigate('/login?redirect=/ride/airport');
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      handleLoginRedirect();
      return;
    }

    if (!arrivalDate || !arrivalTime) {
      setSubmitError('Please fill in arrival date and time');
      return;
    }

    let destination;
    if (destinationType === 'custom') {
      if (!customDestination) {
        setSubmitError('Please enter a destination address');
        return;
      }
      destination = {
        id: 'custom_' + Date.now(),
        name: customDestination.address,
        address: customDestination.fullAddress || customDestination.address,
        parish: customDestination.parish,
        coordinates: customDestination.coordinates,
        type: 'custom'
      };
    } else if (selectedDestination) {
      destination = {
        ...selectedDestination,
        address: selectedDestination.address || selectedDestination.name
      };
    }

    if (!destination) {
      setSubmitError('Please select or enter a destination');
      return;
    }

    const scheduledDateTime = new Date(`${arrivalDate}T${arrivalTime}`);
    
    if (scheduledDateTime <= new Date()) {
      setSubmitError('Please select a future arrival time');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_URL}/api/tourist/ride`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          airport_code: selectedAirport,
          destination: destination,
          flight_number: flightNumber || undefined,
          scheduled_arrival: scheduledDateTime.toISOString(),
          passengers: passengers,
          luggage_count: luggage
        })
      });

      const data = await response.json();

      if (response.status === 401) {
        handleLoginRedirect();
        return;
      }

      if (data.success && data.checkout?.url) {
        window.location.href = data.checkout.url;
      } else if (data.success) {
        setSuccess({
          id: data.ride?.id,
          destination: destination.name,
          scheduledTime: scheduledDateTime,
          priceUSD: rate?.customer_price_usd
        });
      } else {
        setSubmitError(data.error?.message || 'Failed to book airport transfer');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Restore state from localStorage after login
  useEffect(() => {
    const savedState = localStorage.getItem('pendingAirportBooking');
    if (savedState && isAuthenticated) {
      try {
        const state = JSON.parse(savedState);
        if (state.selectedAirport) setSelectedAirport(state.selectedAirport);
        if (state.destinationType) setDestinationType(state.destinationType);
        if (state.selectedDestination) setSelectedDestination(state.selectedDestination);
        if (state.customDestination) setCustomDestination(state.customDestination);
        if (state.flightNumber) setFlightNumber(state.flightNumber);
        if (state.arrivalDate) setArrivalDate(state.arrivalDate);
        if (state.arrivalTime) setArrivalTime(state.arrivalTime);
        if (state.passengers) setPassengers(state.passengers);
        if (state.luggage) setLuggage(state.luggage);
        localStorage.removeItem('pendingAirportBooking');
      } catch (e) {
        console.error('Failed to restore booking state:', e);
      }
    }
  }, [isAuthenticated]);

  // Filter destinations based on type and search
  const filteredDestinations = kinDestinations.filter(dest => {
    const matchesType = destinationType === 'all' || 
                        dest.category === destinationType ||
                        (destinationType === 'hotel' && dest.type === 'hotel') ||
                        (destinationType === 'attraction' && ['museum', 'heritage', 'park', 'historic', 'beach'].includes(dest.type)) ||
                        (destinationType === 'area' && dest.category === 'areas');
    const matchesSearch = !searchQuery || 
                          dest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          dest.parish.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Plane className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Airport Transfer Booked!</h2>
          <p className="text-gray-600 mb-4">
            Your ride to <strong>{success.destination}</strong> is confirmed
          </p>
          <div className="bg-amber-50 rounded-xl p-4 mb-6">
            <p className="text-lg font-semibold text-amber-700">
              {success.scheduledTime.toLocaleDateString('en-JM', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-2xl font-bold text-amber-600">
              {success.scheduledTime.toLocaleTimeString('en-JM', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-3xl font-bold text-green-600 mt-2">${success.priceUSD} USD</p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            A driver will meet you at the arrivals area. Look for your name on a sign.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-amber-600 hover:bg-amber-700">
              Back to Dashboard
            </Button>
            <Button onClick={() => { setSuccess(null); setSelectedDestination(null); setCustomDestination(null); }} variant="outline" className="w-full">
              Book Another Transfer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/rides')} className="p-2 hover:bg-amber-600 rounded-lg transition-colors" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Airport Transfer</h1>
            <p className="text-amber-100 text-sm">Flat-rate USD pricing</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6 max-w-lg mx-auto">
        {/* Airport Selection */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Plane className="w-5 h-5 text-amber-600" />
            Arriving At
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {Object.entries(airports).map(([code, airport]) => (
              <button
                key={code}
                onClick={() => setSelectedAirport(code)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedAirport === code ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`airport-${code}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{airport.name}</p>
                    <p className="text-sm text-gray-500">{airport.address}</p>
                  </div>
                  <span className="text-lg font-bold text-amber-600">{code}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Quick Pick Destinations */}
        {selectedAirport === 'KIN' && quickPicks.length > 0 && !selectedDestination && destinationType !== 'custom' && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Popular Destinations
            </h3>
            <div className="flex flex-wrap gap-2">
              {quickPicks.slice(0, 10).map((pick) => (
                <button
                  key={pick.id}
                  onClick={() => {
                    setSelectedDestination(pick);
                    setDestinationType('all');
                    toast.success(`Selected: ${pick.name} - $${pick.price_usd} USD`);
                  }}
                  className="px-3 py-2 text-sm bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-full border border-amber-200 transition-colors flex items-center gap-1"
                  data-testid={`quick-pick-${pick.id}`}
                >
                  <span>{pick.name}</span>
                  <span className="text-xs font-semibold text-green-600">${pick.price_usd}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Destination Selection */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-600" />
            Going To
          </h3>
          
          {/* Destination Type Tabs */}
          <div className="flex gap-1 mb-4 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'hotel', label: 'Hotels' },
              { key: 'attraction', label: 'Attractions' },
              { key: 'area', label: 'Areas' },
              { key: 'custom', label: 'Custom' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => { 
                  setDestinationType(tab.key); 
                  if (tab.key === 'custom') {
                    setSelectedDestination(null);
                  } else {
                    setCustomDestination(null);
                  }
                }}
                className={`py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                  destinationType === tab.key ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.key === 'custom' && <PenLine className="w-3 h-3 inline mr-1" />}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Input (for non-custom) */}
          {destinationType !== 'custom' && (
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search destinations..."
                  className="pl-9"
                  data-testid="destination-search"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Selected Destination Display */}
          {selectedDestination && destinationType !== 'custom' && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-amber-900">{selectedDestination.name}</p>
                <p className="text-xs text-amber-700">{selectedDestination.parish}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-green-600">${selectedDestination.price_usd}</span>
                <button onClick={() => setSelectedDestination(null)} className="text-amber-500 hover:text-amber-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Curated Destination List */}
          {destinationType !== 'custom' && !selectedDestination && (
            <div className="max-h-60 overflow-y-auto space-y-2">
              {filteredDestinations.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {kinDestinations.length === 0 ? 'Loading destinations...' : 'No destinations match your search'}
                </p>
              ) : (
                filteredDestinations.map((dest) => (
                  <button
                    key={dest.id}
                    onClick={() => setSelectedDestination(dest)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedDestination?.id === dest.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                    }`}
                    data-testid={`destination-${dest.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{dest.name}</p>
                        <p className="text-xs text-gray-500">{dest.parish} · {dest.type}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-green-600">${dest.price_usd}</span>
                        {dest.is_quick_pick && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Custom Address Input */}
          {destinationType === 'custom' && (
            <div className="mt-2">
              <AddressInput
                label="Destination Address"
                value={customDestination}
                onChange={setCustomDestination}
                placeholder="Enter any address in Jamaica"
                theme="light" testIdPrefix="custom-dest"
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter any address - hotels, homes, businesses, or attractions. Rate will be calculated based on parish.
              </p>
            </div>
          )}
        </div>

        {/* Flight Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-600" />
            Flight Details
          </h3>
          
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Flight Number (optional)</label>
            <Input
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
              placeholder="e.g. AA1234"
              className="font-mono"
              data-testid="flight-number-input"
            />
            <p className="text-xs text-gray-500 mt-1">We'll track your flight and adjust pickup time if delayed</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Arrival Date *</label>
              <Input type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)} min={today} data-testid="arrival-date-input" />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Arrival Time *</label>
              <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} data-testid="arrival-time-input" />
            </div>
          </div>
        </div>

        {/* Passengers & Luggage */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex gap-6">
            <div className="flex-1">
              <label className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" /> Passengers
              </label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPassengers(Math.max(1, passengers - 1))} disabled={passengers <= 1}>-</Button>
                <span className="w-8 text-center font-semibold">{passengers}</span>
                <Button variant="outline" size="sm" onClick={() => setPassengers(Math.min(10, passengers + 1))} disabled={passengers >= 10}>+</Button>
              </div>
            </div>
            <div className="flex-1">
              <label className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Luggage
              </label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setLuggage(Math.max(0, luggage - 1))} disabled={luggage <= 0}>-</Button>
                <span className="w-8 text-center font-semibold">{luggage}</span>
                <Button variant="outline" size="sm" onClick={() => setLuggage(Math.min(10, luggage + 1))} disabled={luggage >= 10}>+</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Rate Display */}
        {(selectedDestination || customDestination) && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Transfer Rate
            </h3>
            {rateLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Getting rate...
              </div>
            ) : rateError ? (
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle className="w-4 h-4" />
                {rateError}
              </div>
            ) : rate ? (
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">${rate.customer_price_usd} USD</p>
                <p className="text-sm text-gray-500 mt-1">
                  {rate.destination ? `Flat rate to ${rate.destination}` : `Flat rate from ${selectedAirport} to ${rate.parish || 'destination'}`}
                </p>
                <p className="text-xs text-gray-400 mt-2">All-inclusive - no hidden fees</p>
              </div>
            ) : null}
          </div>
        )}


        {/* Interactive Route Map */}
        {(selectedDestination || customDestination) && (
          <RideMapPreview
            pickup={{
              street: airports[selectedAirport]?.name || selectedAirport,
              coordinates: airports[selectedAirport]?.coordinates || null
            }}
            dropoff={{
              street: selectedDestination?.name || customDestination?.address || 'Destination',
              coordinates: selectedDestination?.coordinates || customDestination?.coordinates || null
            }}
            height={280}
            draggable={false}
          />
        )}

        {/* Login Prompt for Unauthenticated */}
        {!isAuthenticated && rate && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">Sign in to book</p>
                <p className="text-amber-700 text-sm mt-1">
                  Your transfer is ${rate.customer_price_usd} USD. Sign in to complete your booking.
                </p>
                <Button onClick={handleLoginRedirect} className="mt-3 bg-amber-600 hover:bg-amber-700 text-white" data-testid="login-to-book-btn">
                  Sign in to Book
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{submitError}</p>
          </div>
        )}

        {/* Submit Button */}
        {isAuthenticated && (
          <Button
            onClick={handleSubmit}
            disabled={submitting || rateLoading || !rate || (!selectedDestination && !customDestination)}
            className="w-full py-6 text-lg bg-amber-600 hover:bg-amber-700"
            data-testid="book-transfer-btn"
          >
            {submitting ? 'Processing...' : `Book Transfer - $${rate?.customer_price_usd || '...'} USD`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AirportRide;
